'use client';

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { startSessionMonitor, stopSessionMonitor, getSessionTimeoutMs } from '@/lib/session-manager';
import { useToast } from './ToastContext';

interface SessionContextValue {
  timeoutMs: number;
  setTimeoutMs: (ms: number) => void;
  resetTimer: () => void;
}

const SessionContext = createContext<SessionContextValue>({
  timeoutMs: 30 * 60 * 1000,
  setTimeoutMs: () => {},
  resetTimer: () => {},
});

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const { operator, logout } = useAuth();
  const toast = useToast();
  const [timeoutMs, setTimeoutMsState] = useState(getSessionTimeoutMs);
  const warningShownRef = useRef(false);

  const handleExpire = useCallback(() => {
    if (operator) {
      toast.warning('Sessione scaduta per inattività. Effettua nuovamente il login.');
      logout();
    }
  }, [operator, logout, toast]);

  useEffect(() => {
    if (!operator) {
      stopSessionMonitor();
      return;
    }
    if (timeoutMs === 0) {
      stopSessionMonitor();
      return;
    }
    warningShownRef.current = false;
    startSessionMonitor(handleExpire);
    return () => stopSessionMonitor();
  }, [operator, timeoutMs, handleExpire]);

  const setTimeoutMs = useCallback((ms: number) => {
    setTimeoutMsState(ms);
    if (typeof window !== 'undefined') {
      localStorage.setItem('lcm_session_timeout_ms', String(ms));
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('lcm_last_activity', String(Date.now()));
    }
  }, []);

  return (
    <SessionContext.Provider value={{ timeoutMs, setTimeoutMs, resetTimer }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  return useContext(SessionContext);
}
